import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { of } from 'rxjs';
import { filter, map, switchMap, take, takeWhile } from 'rxjs/operators';
import { DirectoryOrganisationUnitColumnType } from 'src/app/samwebDescription/samweb/directory/directory-column-type';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { DialTarget } from 'src/app/store/samwin/next-dial-target/next-dial-target.state';
import { DirectorySettingsState } from '../../../../brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { GridSortSettingsChanged } from '../../../brick-sort-state/grid-sort.actions';
import { QueryDirectory } from '../directory-data/directory-data-behaviour.actions';
import { DirectoryDataBehaviourState } from '../directory-data/directory-data-behaviour.state';
import {
   ClearAndStartLoadingDirectoryValues,
   DirectoryBrowserCallFromColumn,
   DirectoryBrowserChangeCustomSorting,
   DirectoryBrowserEntrySelected,
   DirectoryBrowserMakeCall,
   DirectoryBrowserSelectNextEntry,
   DirectoryRowDoubleClicked,
   DirectorySearchInGivenColumns,
   DirectorySearchValueChanged,
   LoadMoreDirectoryValues,
   SetTemporarySearchColumns,
} from './directory-browser-behaviour.actions';
import { DirectoryBrowserBehaviourSelectors } from './directory-browser-behaviour.selectors';
import { DirectoryBrowserBehaviourModel, DirectoryBrowserBehaviourState } from './directory-browser-behaviour.state';

@Injectable()
export class DirectoryBrowserBehaviourHandlers {
   constructor(public store: Store) {}

   @Action(DirectoryBrowserSelectNextEntry)
   @ImmutableContext()
   public directoryBrowserSelectNextEntry(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserSelectNextEntry) {
      const directoryState = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault)(action.brickInstanceId);
      const currentValues = this.store.selectSnapshot(DirectoryDataBehaviourState.getDirectoryEntries)(action.brickInstanceId);
      let currentIndex = _(currentValues).findIndex((value) => value.id === directoryState.selectedDirectory);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      const selectedDirectory = currentValues[nextIndex]?.id;
      if (_.isNil(selectedDirectory)) {
         return;
      }
      return ctx.dispatch(new DirectoryBrowserEntrySelected(action.brickInstanceId, selectedDirectory));
   }

   @Action(DirectoryBrowserCallFromColumn)
   @ImmutableContext()
   public directoryBrowserCallFromColumn(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserCallFromColumn) {
      const selectedNumber$ = this.store
         .select(DirectoryBrowserBehaviourSelectors.getSelectedNumberFromColumn)
         .pipe(map((getSelectedNumber) => getSelectedNumber(action.brickInstanceId, action.columnName)));

      const isSearching$ = this.store.select(DirectoryDataBehaviourState.wasSearchExecuted).pipe(map((isSearching) => !isSearching(action.brickInstanceId)));

      const callEntry$ = isSearching$
         .pipe(
            switchMap((isSearching) =>
               selectedNumber$.pipe(
                  switchMap((selectedNumber) => {
                     if (!isSearching) {
                        const instance = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault)(action.brickInstanceId);
                        if (!_.isEmpty(selectedNumber)) {
                           return ctx.dispatch(new StartNewCallAction(<DialTarget>{ number: selectedNumber, subscriberId: instance.selectedDirectory }));
                        }
                        return ctx.dispatch(new StartNewCallAction(instance.searchValue));
                     }
                     return of(null);
                  }),
                  map(() => isSearching)
               )
            )
         )
         .pipe(takeWhile((isSearching) => isSearching));

      return callEntry$;
   }

   @Action(DirectorySearchInGivenColumns)
   @ImmutableContext()
   public directorySearchInGivenColumns(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectorySearchInGivenColumns) {
      // check given columns for being configured as searchable
      const currentSearchColumnKeys = action.searchColumns.map((x) => x.directoryColumnKey);
      const searchableColumnKeys = this.store.selectSnapshot(DirectorySettingsState.getDirectorySearchableColumnKeys)(action.brickInstanceId);
      const isAnyColumnSearchable = currentSearchColumnKeys.some((directoryColumnKey) => searchableColumnKeys.includes(directoryColumnKey));
      if (!isAnyColumnSearchable) {
         // none of the given columns is configured for search, ignore request
         return;
      }

      const directoryState = this.store.selectSnapshot(DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault)(action.brickInstanceId);
      if (
         (_.isEmpty(action.searchValue) || directoryState.searchValue === action.searchValue) &&
         _.isEqual(directoryState.temporarySearchColumns, action.searchColumns)
      ) {
         ctx.dispatch(new SetTemporarySearchColumns(action.brickInstanceId, []));
      } else {
         ctx.dispatch(new SetTemporarySearchColumns(action.brickInstanceId, action.searchColumns));
      }
      ctx.dispatch(new DirectorySearchValueChanged(action.brickInstanceId, action.searchValue, true));
   }

   @Action(DirectoryBrowserChangeCustomSorting)
   @ImmutableContext()
   public directoryBrowserChangeCustomSorting(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserChangeCustomSorting) {
      const searchableColumns = this.store.selectSnapshot(DirectorySettingsState.getAllPossibleSearchableColumns).map((x) => x.directoryColumnKey);
      const customSorting = action.customSorting.filter((x) => searchableColumns.includes(x.field));
      if (_.isEmpty(customSorting)) {
         return;
      }
      ctx.dispatch(new GridSortSettingsChanged(action.brickInstanceId, _(customSorting).head()));
      return ctx.dispatch(new ClearAndStartLoadingDirectoryValues(action.brickInstanceId));
   }

   @Action(DirectoryRowDoubleClicked)
   public directoryRowDoubleClicked(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryRowDoubleClicked) {
      if (!this.store.selectSnapshot(DirectorySettingsState.getStartNewCallOnDoubleClick)(action.brickInstanceId)) {
         return;
      }
      const rowContent = this.store.selectSnapshot(DirectorySettingsState.getSelectedOrDefaultCallColumn)(
         action.brickInstanceId,
         action.item,
         action.columnFieldId
      );
      if (_.isEmpty(rowContent?.value)) {
         return;
      }
      return ctx.dispatch(
         new StartNewCallAction({
            number: rowContent.value.toString(),
            subscriberId: action.item.id,
         })
      );
   }

   @Action(DirectoryBrowserMakeCall)
   @ImmutableContext()
   public directoryBrowserMakeCall(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: DirectoryBrowserMakeCall) {
      const selectedEntry = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSelectedEntry)(action.brickInstanceId);
      const directoryColumn = this.store.selectSnapshot(DirectorySettingsState.getDefaultCallColumnWithValue)(action.brickInstanceId, selectedEntry);
      if (_.isNil(directoryColumn) || _.isUndefined(directoryColumn)) {
         return;
      }
      const dirKey = DirectoryOrganisationUnitColumnType.valueByColumnKey(directoryColumn.organisationUnitColumnKey).directoryMapValue;
      return ctx.dispatch(new DirectoryBrowserCallFromColumn(action.brickInstanceId, dirKey));
   }

   @Action(LoadMoreDirectoryValues)
   public loadMoreDirectoryValues(ctx: StateContext<DirectoryBrowserBehaviourModel>, action: LoadMoreDirectoryValues) {
      if (!this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getIsQueryingDirectoryPossible)(action.brickInstanceId)) {
         return;
      }

      const restrictionFilter = this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getDirectoryRestrictions)(action.brickInstanceId);

      if (_.isNil(restrictionFilter)) {
         return this.store.select(DirectoryBrowserBehaviourSelectors.getDirectoryRestrictions).pipe(
            map((fn) => fn(action.brickInstanceId)),
            filter((x) => !_.isNil(x)),
            take(1),
            switchMap(() => this.store.dispatch(new LoadMoreDirectoryValues(action.brickInstanceId)))
         );
      }

      ctx.dispatch(
         new QueryDirectory(
            action.brickInstanceId,
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSearchValuesForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getPagingForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSortColumnsForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSortDirectionForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSearchableColumnsForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getOrganisationRestrictionsForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourSelectors.getSubscriberFilterForQuery)(action.brickInstanceId),
            this.store.selectSnapshot(DirectoryBrowserBehaviourState.getDirectoryMatchingPercentage)(action.brickInstanceId)
         )
      );
   }
}
